import { FormNode, GenericFieldValue } from '@msdk/definitions'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

import { ChangeArgs } from '../models/form'
import { useDebounce } from './useDebounce'

/**
 * This function returns a stateful value and a function to update it, and also triggers an onChange
 * function when the value is changed and has been debounced.
 * @param {FormNode} node - A FormNode object that represents a field in a form. It contains
 * information about the field's value, validation status, and other metadata.
 * @param onChange - onChange is a function that takes an argument of type ChangeArgs and returns void.
 * It is used to handle changes to the field value and update the form state accordingly.
 * @returns The function `useFieldValue` returns an array with two elements: `[GenericFieldValue,
 * Dispatch<SetStateAction<GenericFieldValue>>]`. The first element is the current value of the field,
 * and the second element is a function to update the value of the field.
 */
export function useFieldValue(
  node: FormNode,
  onChange: (args: ChangeArgs) => void,
  delay?: number,
): [GenericFieldValue, Dispatch<SetStateAction<GenericFieldValue>>] {
  const [value, setValue] = useState<GenericFieldValue>(null)
  const debouncedValue = useDebounce(value, delay ?? 300)
  /**
   * Tracks whether the current value change originated from user input.
   * When true, prevents external updates from overwriting the user's changes
   * while the debounced value is being processed.
   */
  const isLocalChangeRef = useRef(false)
  const currentValueRef = useRef<GenericFieldValue>(null)
  const pendingRef = useRef<{ value: GenericFieldValue }>()
  const lastSentRef = useRef<{ value: GenericFieldValue }>()

  const flush = () => {
    if (!pendingRef.current) {
      return
    }

    const { value: pendingValue } = pendingRef.current
    pendingRef.current = undefined
    lastSentRef.current = { value: pendingValue }
    isLocalChangeRef.current = true
    onChange({ value: pendingValue, fieldName: node.id })
  }

  useEffect(() => {
    node.value.flushChange = flush
    return () => {
      if (node.value.flushChange === flush) {
        node.value.flushChange = undefined
      }
    }
  })

  useEffect(() => {
    if (!isLocalChangeRef.current) {
      setValue(node.value.data)
      currentValueRef.current = node.value.data
    }
    isLocalChangeRef.current = false
  }, [node.value.data])

  useEffect(() => {
    if (node.value.isDirty && debouncedValue === value && pendingRef.current?.value === debouncedValue) {
      flush()
    } else if (node.value.isDirty && debouncedValue === node.value.data && !lastSentRef.current) {
      pendingRef.current = { value: debouncedValue }
      flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  const setFieldValue: Dispatch<SetStateAction<GenericFieldValue>> = (next) => {
    const nextValue = typeof next === 'function' ? next(currentValueRef.current) : next
    currentValueRef.current = nextValue
    pendingRef.current = { value: nextValue }
    setValue(nextValue)

    // Once exposed, revalidate corrections immediately instead of waiting for debounce.
    if (node.value.validationRevealed) {
      flush()
    }
  }

  return [value, setFieldValue]
}
